<template>
  <div class="action-reasons-container">
    <div class="action-reasons-header">
      <p>Action Reasons Timeline: Hover over ticks to see action reasons</p>
    </div>
    
    <LoadingSpinner v-if="isLoading" />
    
    <div v-else class="timeline-container">
      <div class="timeline-wrapper">
        <!-- Timeline axis line -->
        <div class="timeline-axis"></div>
        
        <!-- Timeline ticks -->
        <div class="timeline-ticks">
          <div 
            v-for="(tick, index) in timelineDots" 
            :key="index"
            class="timeline-tick"
            :class="{ 
              'has-action': tick.hasAction,
              'is-updated': tick.isUpdated === 1,
              'not-updated': tick.isUpdated === 0,
              'major-tick': shouldShowTimeLabel(index),
              'active': tick.isHovered 
            }"
            @mouseenter="showTooltip(index, $event)"
            @touchstart="showTooltip(index, $event)"
            @click="toggleTooltip(index, $event)"
          >
          </div>
        </div>
        
        <!-- Time labels -->
        <div class="time-labels">
          <div 
            v-for="(tick, index) in timelineDots" 
            :key="`label-${index}`"
            class="time-label"
            :class="{ 'show-label': shouldShowTimeLabel(index) }"
          >
            {{ shouldShowTimeLabel(index) ? tick.time : '' }}
          </div>
        </div>
      </div>
      
      <!-- Tooltip -->
      <div 
        v-if="activeTooltip"
        class="tooltip"
        :class="{ 'dragging': isDragging, [`size-${modalSize}`]: true }"
        :style="tooltipStyle"
      >
        <div class="tooltip-content">
          <div 
            class="tooltip-header"
            @mousedown="startDrag"
            @touchstart="startDrag"
          >
            <div class="tooltip-header-left">
              <div class="drag-handle" title="Drag to move">
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="2" cy="2" r="1" fill="currentColor"/>
                  <circle cx="6" cy="2" r="1" fill="currentColor"/>
                  <circle cx="10" cy="2" r="1" fill="currentColor"/>
                  <circle cx="2" cy="6" r="1" fill="currentColor"/>
                  <circle cx="6" cy="6" r="1" fill="currentColor"/>
                  <circle cx="10" cy="6" r="1" fill="currentColor"/>
                  <circle cx="2" cy="10" r="1" fill="currentColor"/>
                  <circle cx="6" cy="10" r="1" fill="currentColor"/>
                  <circle cx="10" cy="10" r="1" fill="currentColor"/>
                </svg>
              </div>
              <h4>{{ activeTooltip.time }}</h4>
            </div>
            <div class="tooltip-header-center">
              <span class="drag-hint">Drag to move</span>
            </div>
            <button class="close-button" @click="hideTooltip" aria-label="Close">×</button>
          </div>
          
          <!-- Size Controls -->
          <div class="size-controls">
            <span class="size-controls-label">Window Size:</span>
            <button 
              class="size-button"
              :class="{ 'active': modalSize === 'small' }"
              @click="setModalSize('small')"
              title="Set window size to Small"
            >
              S
            </button>
            <button 
              class="size-button"
              :class="{ 'active': modalSize === 'medium' }"
              @click="setModalSize('medium')"
              title="Set window size to Medium"
            >
              M
            </button>
            <button 
              class="size-button"
              :class="{ 'active': modalSize === 'large' }"
              @click="setModalSize('large')"
              title="Set window size to Large"
            >
              L
            </button>
            <button 
              class="size-button"
              :class="{ 'active': modalSize === 'full' }"
              @click="setModalSize('full')"
              title="Set window size to Full"
            >
              XL
            </button>
          </div>
          
          <pre v-if="activeTooltip.reason" class="reason-text">{{ formatReasonText(activeTooltip.reason) }}</pre>
          <p v-else class="no-action">No action recorded for this time</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { apiRequest } from '@/services/apiService';
import { getDevModeState } from '@/services/cookieService';
import LoadingSpinner from './LoadingSpinner.vue';

export default {
  name: 'ActionReasons',
  components: {
    LoadingSpinner
  },
  props: {
    plantId: {
      type: Number,
      required: true
    },
    date: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      isLoading: false,
      actionReasonsData: [],
      timelineDots: [],
      activeTooltip: null,
      tooltipStyle: {
        left: '0px',
        top: '0px'
      },
      subDataUrl: '',
      hideTooltipTimer: null,
      lastTouchIndex: null,
      isDragging: false,
      dragOffset: { x: 0, y: 0 },
      initialTooltipPosition: { left: 0, top: 0 },
      modalSize: 'medium',
      sizePresets: {
        small: { width: 400, height: 300 },
        medium: { width: 700, height: 450 },
        large: { width: 1000, height: 600 },
        full: { width: '95vw', height: '80vh' }
      },
      isScrolling: false,
      scrollTimeout: null
    };
  },
  watch: {
    plantId: {
      handler() {
        this.fetchActionReasons();
      },
      immediate: false
    },
    date: {
      handler() {
        this.fetchActionReasons();
      },
      immediate: false
    }
  },
  mounted() {
    this.initializeTimeline();
    this.setupUrl();
    this.loadSizePreference(); // Load saved modal size preference
    this.fetchActionReasons();
    
    // Add click outside handler for mobile
    document.addEventListener('click', this.handleClickOutside);
    
    // Add resize listener for responsive sizing
    window.addEventListener('resize', this.handleResize);
    this.handleResize(); // Initial call
    
    // Add scroll listener to reposition tooltip if needed
    window.addEventListener('scroll', this.handleScroll, true);
    
    // Add drag event listeners
    document.addEventListener('mousemove', this.onDrag);
    document.addEventListener('mouseup', this.stopDrag);
    document.addEventListener('touchmove', this.onDrag, { passive: false });
    document.addEventListener('touchend', this.stopDrag);
  },
  
  beforeUnmount() {
    if (this.hideTooltipTimer) {
      clearTimeout(this.hideTooltipTimer);
    }
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
    // Remove event listeners
    document.removeEventListener('click', this.handleClickOutside);
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('scroll', this.handleScroll, true);
    document.removeEventListener('mousemove', this.onDrag);
    document.removeEventListener('mouseup', this.stopDrag);
    document.removeEventListener('touchmove', this.onDrag);
    document.removeEventListener('touchend', this.stopDrag);
  },
  methods: {
    setupUrl() {
      const devModeState = getDevModeState();
      this.subDataUrl = devModeState.isDevMode ? "/dev-api/api/v1.1/data" : "/api/v1.1/data";
    },
    
    initializeTimeline() {
      this.timelineDots = [];
      for (let i = 0; i < 288; i++) {
        const minutes = i * 5;
        const hours = Math.floor(minutes / 60).toString().padStart(2, '0');
        const mins = (minutes % 60).toString().padStart(2, '0');
        const timeString = `${hours}:${mins}`;
        
        this.timelineDots.push({
          index: i,
          time: timeString,
          hasAction: false,
          reason: '',
          isHovered: false,
          isUpdated: null,
          tooltipText: `${timeString} - No action recorded`
        });
      }
      
      // Debug: Log first and last times to verify range
    //   console.log('Timeline initialized:', {
    //     totalDots: this.timelineDots.length,
    //     firstTime: this.timelineDots[0]?.time,
    //     lastTime: this.timelineDots[this.timelineDots.length - 1]?.time
    //   });
    },
    
    async fetchActionReasons() {
      if (!this.plantId || !this.date) {
        return;
      }
      
      try {
        this.isLoading = true;
        
        const bodyData = {
          plant_id: this.plantId,
          date: this.date
        };
        
        const url = `${this.subDataUrl}/action_reason/get`;
        
        const response = await apiRequest('post', url, bodyData);
        
        if (response.errorCode === 0 && response.data && response.data.action_reasons) {
          this.processActionReasons(response.data.action_reasons);
        } else {
        //   console.warn('No action reasons data received or error occurred');
          this.resetTimeline();
        }
      } catch (error) {
        // console.error('Error fetching action reasons:', error);
        this.resetTimeline();
      } finally {
        this.isLoading = false;
      }
    },
    
    processActionReasons(actionReasons) {
      // Reset timeline
      this.resetTimeline();
      
      // console.log('Processing', actionReasons.length, 'action reasons');
      
      // Process each action reason
      actionReasons.forEach((action, index) => {
        try {
          // Extract time directly from the ISO string to avoid timezone conversion
          // Format: "2025-10-16T00:00:00+11:00" -> extract "00:00"
          const isoString = action.date_added;
          const timeMatch = isoString.match(/T(\d{2}):(\d{2})/);
          
          if (!timeMatch) {
            // console.warn(`Invalid date format at index ${index}:`, isoString);
            return;
          }
          
          const hours = timeMatch[1];
          const minutes = timeMatch[2];
          const timeString = `${hours}:${minutes}`;
          
          // console.log(`Processing action ${index + 1}: ${isoString} -> ${timeString}`);
          
          // Find the corresponding dot index
          const dotIndex = this.findDotIndexByTime(timeString);
          
          if (dotIndex !== -1) {
            this.timelineDots[dotIndex].hasAction = true;
            this.timelineDots[dotIndex].reason = action.reason_text;
            this.timelineDots[dotIndex].power = action.power;
            this.timelineDots[dotIndex].isUpdated = action.is_updated !== undefined ? action.is_updated : null;
            this.timelineDots[dotIndex].tooltipText = `${timeString} - Power: ${action.power}kW`;
            
            // console.log(`✓ Mapped to dot ${dotIndex}`);
          } else {
            // console.warn(`✗ No dot found for time: ${timeString}`);
          }
        } catch (error) {
          console.error(`Error processing action ${index}:`, error, action);
        }
      });
      
      const mappedCount = this.timelineDots.filter(d => d.hasAction).length;
    //   console.log(`Timeline complete: ${mappedCount}/${actionReasons.length} actions mapped`);
    },
    
    findDotIndexByTime(timeString) {
      return this.timelineDots.findIndex(dot => dot.time === timeString);
    },
    
    resetTimeline() {
      this.timelineDots.forEach(dot => {
        dot.hasAction = false;
        dot.reason = '';
        dot.isUpdated = null;
        dot.tooltipText = `${dot.time} - No action recorded`;
      });
    },
    
    showTooltip(index, event) {
      const tick = this.timelineDots[index];
      const tickElement = event.target;
      const rect = tickElement.getBoundingClientRect();
      
      // Check if tooltip is already shown
      const isFirstShow = !this.activeTooltip;
      
      // Update tooltip content
      this.activeTooltip = {
        time: tick.time,
        reason: tick.reason
      };
      
      // Only position tooltip on first show, not when hovering over different ticks
      if (isFirstShow) {
        // Reset drag state when showing new tooltip
        this.isDragging = false;
        this.dragOffset = { x: 0, y: 0 };
        
        // Check if mobile device
        const isMobile = window.innerWidth <= 750;
        
        if (isMobile) {
          // For mobile, position tooltip at safe position within viewport
          const viewportHeight = window.innerHeight;
          const sizePreset = this.sizePresets[this.modalSize];
          let tooltipHeight = sizePreset.height;
          
          // Convert vh to pixels if needed
          if (typeof tooltipHeight === 'string' && tooltipHeight.includes('vh')) {
            tooltipHeight = (parseFloat(tooltipHeight) / 100) * viewportHeight;
          }
          
          // Position at top of viewport with padding, ensuring it's fully visible
          const topPosition = Math.max(10, Math.min(20, (viewportHeight - tooltipHeight) / 2));
          
          this.tooltipStyle = {
            left: '50%',
            top: `${topPosition}px`,
            transform: 'translateX(-50%) translateY(0%)'
          };
        } else {
          // Desktop smart positioning logic
          this.positionTooltipDesktop(rect, tick);
        }
        
        // Store initial position for drag calculations
        this.$nextTick(() => {
          const tooltipElement = document.querySelector('.tooltip');
          if (tooltipElement) {
            const tooltipRect = tooltipElement.getBoundingClientRect();
            this.initialTooltipPosition = {
              left: tooltipRect.left,
              top: tooltipRect.top
            };
          }
        });
      }
      // If tooltip already exists, just update content without repositioning
    },
    
    positionTooltipDesktop(tickRect, tick) {
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      // Get dimensions from size preset
      const sizePreset = this.sizePresets[this.modalSize];
      let estimatedTooltipWidth = sizePreset.width;
      let tooltipHeight = sizePreset.height;
      
      // Convert viewport units to pixels if needed
      if (typeof estimatedTooltipWidth === 'string' && estimatedTooltipWidth.includes('vw')) {
        estimatedTooltipWidth = (parseFloat(estimatedTooltipWidth) / 100) * viewportWidth;
      }
      if (typeof tooltipHeight === 'string' && tooltipHeight.includes('vh')) {
        tooltipHeight = (parseFloat(tooltipHeight) / 100) * viewportHeight;
      }
      
      // Get timeline container position for reference
      const timelineContainer = document.querySelector('.timeline-container');
      const containerRect = timelineContainer ? timelineContainer.getBoundingClientRect() : tickRect;
      
      // Store tick position for potential arrow positioning
      this.activeTooltip.tickLeft = tickRect.left + tickRect.width / 2;
      
      // Calculate horizontal position (centered on tick)
      let left = tickRect.left + tickRect.width / 2;
      
      // Check horizontal boundaries and adjust if needed
      const leftBoundary = estimatedTooltipWidth / 2 + 10;
      const rightBoundary = viewportWidth - estimatedTooltipWidth / 2 - 10;
      
      if (left < leftBoundary) {
        left = leftBoundary;
      } else if (left > rightBoundary) {
        left = rightBoundary;
      }
      
      // Smart vertical positioning - always keep within viewport
      let top, transform;
      const padding = 10; // Padding from viewport edges
      const spaceAbove = containerRect.top - padding;
      const spaceBelow = viewportHeight - containerRect.bottom - padding;
      
      // Priority: above ticks, but ensure tooltip is fully visible in viewport
      if (spaceAbove >= tooltipHeight) {
        // Enough space above - position above the timeline
        top = containerRect.top - 15;
        transform = 'translateX(-50%) translateY(-100%)';
      } else if (spaceBelow >= tooltipHeight) {
        // Not enough space above, but enough below - position below timeline
        top = containerRect.bottom + 15;
        transform = 'translateX(-50%) translateY(0%)';
      } else {
        // Limited space both ways - center vertically in viewport
        // This ensures the tooltip is always fully visible
        top = (viewportHeight - tooltipHeight) / 2;
        transform = 'translateX(-50%) translateY(0%)';
      }
      
      // Final safety check: ensure tooltip doesn't go outside viewport bounds
      // Calculate actual top position after transform
      let finalTop = top;
      if (transform.includes('translateY(-100%)')) {
        finalTop = top - tooltipHeight;
      }
      
      // Constrain to viewport
      if (finalTop < padding) {
        top = padding;
        transform = 'translateX(-50%) translateY(0%)';
      } else if (finalTop + tooltipHeight > viewportHeight - padding) {
        top = viewportHeight - tooltipHeight - padding;
        transform = 'translateX(-50%) translateY(0%)';
      }
      
      this.tooltipStyle = {
        left: `${left}px`,
        top: `${top}px`,
        transform: transform
      };
    },
    
    hideTooltip() {
      this.activeTooltip = null;
      if (this.hideTooltipTimer) {
        clearTimeout(this.hideTooltipTimer);
        this.hideTooltipTimer = null;
      }
    },
    
    toggleTooltip(index, event) {
      // Toggle tooltip on click/tap for all devices
      event.preventDefault();
      event.stopPropagation();
      
      // Clear any existing timer
      if (this.hideTooltipTimer) {
        clearTimeout(this.hideTooltipTimer);
        this.hideTooltipTimer = null;
      }
      
      if (this.lastTouchIndex === index && this.activeTooltip) {
        this.hideTooltip();
        this.lastTouchIndex = null;
      } else {
        this.showTooltip(index, event);
        this.lastTouchIndex = index;
      }
    },
    
    shouldShowTimeLabel(index) {
      // Show time labels every 12 dots (every hour) - exactly at hour marks
      return index % 12 === 0;
    },
    
    formatReasonText(reasonText) {
      if (!reasonText) return 'No action reason available';
      
      try {
        // Try to parse as JSON and format it
        const parsed = JSON.parse(reasonText);
        return JSON.stringify(parsed, null, 2);
      } catch (e) {
        // If not valid JSON, check if it looks like a JSON string and try to clean it
        if (reasonText.includes('{') && reasonText.includes('}')) {
          try {
            // Try to extract and format JSON-like content
            const cleanedText = reasonText
              .replace(/'/g, '"')  // Replace single quotes with double quotes
              .replace(/(\w+):/g, '"$1":')  // Add quotes around keys
              .replace(/,\s*}/g, '}')  // Remove trailing commas
              .replace(/,\s*]/g, ']'); // Remove trailing commas in arrays
            
            const parsed = JSON.parse(cleanedText);
            return JSON.stringify(parsed, null, 2);
          } catch (e2) {
            // If still fails, return formatted but not as JSON
            return reasonText
              .replace(/,\s*/g, ',\n  ')
              .replace(/{/g, '{\n  ')
              .replace(/}/g, '\n}')
              .replace(/\[/g, '[\n  ')
              .replace(/]/g, '\n]');
          }
        }
        
        // For non-JSON text, just return as is with some basic formatting
        return reasonText
          .replace(/\.\s+/g, '.\n\n')  // Add line breaks after sentences
          .replace(/;\s+/g, ';\n')     // Add line breaks after semicolons
          .trim();
      }
    },
    
    handleClickOutside(event) {
      // Only handle on mobile and only for actual clicks (not scroll events)
      // Don't close if user is currently scrolling
      if (window.innerWidth <= 750 && this.activeTooltip && event.type === 'click' && !this.isScrolling) {
        const timelineContainer = this.$el.querySelector('.timeline-container');
        const tooltip = this.$el.querySelector('.tooltip');
        
        // Check if click is outside both timeline and tooltip
        if (timelineContainer && !timelineContainer.contains(event.target) && 
            (!tooltip || !tooltip.contains(event.target))) {
          this.hideTooltip();
          this.lastTouchIndex = null;
        }
      }
    },
    
    handleResize() {
      // Force re-render on desktop to recalculate spacing
      if (window.innerWidth > 750) {
        this.$forceUpdate();
      }
      
      // Reposition tooltip if it's open to ensure it stays within viewport
      if (this.activeTooltip) {
        this.$nextTick(() => {
          const tooltipElement = document.querySelector('.tooltip');
          if (!tooltipElement) return;
          
          const tooltipRect = tooltipElement.getBoundingClientRect();
          const viewportWidth = window.innerWidth;
          const viewportHeight = window.innerHeight;
          const padding = 10;
          
          // Check if tooltip is outside viewport bounds
          const isOutOfBounds = 
            tooltipRect.left < padding ||
            tooltipRect.right > viewportWidth - padding ||
            tooltipRect.top < padding ||
            tooltipRect.bottom > viewportHeight - padding;
          
          if (isOutOfBounds) {
            // Recalculate position to fit within viewport
            let newLeft = Math.max(padding, Math.min(tooltipRect.left, viewportWidth - tooltipRect.width - padding));
            let newTop = Math.max(padding, Math.min(tooltipRect.top, viewportHeight - tooltipRect.height - padding));
            
            this.tooltipStyle = {
              left: `${newLeft + tooltipRect.width / 2}px`,
              top: `${newTop}px`,
              transform: 'translateX(-50%) translateY(0%)'
            };
          }
        });
      }
    },
    
    handleScroll() {
      // Reposition tooltip when user scrolls to keep it within viewport
      // Don't close the tooltip, just reposition it
      if (this.activeTooltip && !this.isDragging) {
        // Mark as scrolling to prevent click outside handler from triggering
        this.isScrolling = true;
        
        // Clear any existing scroll timeout
        if (this.scrollTimeout) {
          clearTimeout(this.scrollTimeout);
        }
        
        // Clear any pending hide timers to prevent auto-close during scroll
        if (this.hideTooltipTimer) {
          clearTimeout(this.hideTooltipTimer);
          this.hideTooltipTimer = null;
        }
        
        // Reset scrolling flag after scroll ends
        this.scrollTimeout = setTimeout(() => {
          this.isScrolling = false;
        }, 150);
        
        this.$nextTick(() => {
          const tooltipElement = document.querySelector('.tooltip');
          if (!tooltipElement) return;
          
          const tooltipRect = tooltipElement.getBoundingClientRect();
          const viewportWidth = window.innerWidth;
          const viewportHeight = window.innerHeight;
          const padding = 10;
          
          // Check if tooltip is outside viewport bounds
          const isOutOfBounds = 
            tooltipRect.left < padding ||
            tooltipRect.right > viewportWidth - padding ||
            tooltipRect.top < padding ||
            tooltipRect.bottom > viewportHeight - padding;
          
          if (isOutOfBounds) {
            // Recalculate position to fit within viewport
            let newLeft = Math.max(padding, Math.min(tooltipRect.left, viewportWidth - tooltipRect.width - padding));
            let newTop = Math.max(padding, Math.min(tooltipRect.top, viewportHeight - tooltipRect.height - padding));
            
            this.tooltipStyle = {
              left: `${newLeft + tooltipRect.width / 2}px`,
              top: `${newTop}px`,
              transform: 'translateX(-50%) translateY(0%)'
            };
          }
        });
      }
    },
    
    startDrag(event) {
      // Don't start drag if clicking the close button
      if (event.target.classList.contains('close-button') || event.target.closest('.close-button')) {
        return;
      }
      
      event.preventDefault();
      event.stopPropagation();
      this.isDragging = true;
      
      const tooltipElement = document.querySelector('.tooltip');
      if (!tooltipElement) return;
      
      const tooltipRect = tooltipElement.getBoundingClientRect();
      const clientX = event.touches ? event.touches[0].clientX : event.clientX;
      const clientY = event.touches ? event.touches[0].clientY : event.clientY;
      
      // Calculate offset from mouse/touch position to tooltip top-left corner
      this.dragOffset = {
        x: clientX - tooltipRect.left,
        y: clientY - tooltipRect.top
      };
      
      // Store initial position
      this.initialTooltipPosition = {
        left: tooltipRect.left,
        top: tooltipRect.top
      };
      
      // Convert transform-based positioning to absolute positioning for dragging
      // This ensures smooth dragging regardless of initial positioning method
      this.tooltipStyle = {
        ...this.tooltipStyle,
        left: `${tooltipRect.left}px`,
        top: `${tooltipRect.top}px`,
        transform: 'none'
      };
    },
    
    onDrag(event) {
      if (!this.isDragging || !this.activeTooltip) return;
      
      event.preventDefault();
      
      const clientX = event.touches ? event.touches[0].clientX : event.clientX;
      const clientY = event.touches ? event.touches[0].clientY : event.clientY;
      
      // Calculate new position
      let newLeft = clientX - this.dragOffset.x;
      let newTop = clientY - this.dragOffset.y;
      
      // Get tooltip dimensions to constrain within viewport
      const tooltipElement = document.querySelector('.tooltip');
      if (!tooltipElement) return;
      
      const tooltipRect = tooltipElement.getBoundingClientRect();
      const tooltipWidth = tooltipRect.width;
      const tooltipHeight = tooltipRect.height;
      
      // Constrain to viewport boundaries
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      newLeft = Math.max(0, Math.min(newLeft, viewportWidth - tooltipWidth));
      newTop = Math.max(0, Math.min(newTop, viewportHeight - tooltipHeight));
      
      // Update tooltip position (remove transform when dragging)
      this.tooltipStyle = {
        left: `${newLeft}px`,
        top: `${newTop}px`,
        transform: 'none'
      };
    },
    
    stopDrag() {
      if (this.isDragging) {
        this.isDragging = false;
      }
    },
    
    loadSizePreference() {
      try {
        const savedSize = localStorage.getItem('actionReasons_modalSize');
        if (savedSize && this.sizePresets[savedSize]) {
          this.modalSize = savedSize;
        }
      } catch (error) {
        console.warn('Failed to load modal size preference:', error);
      }
    },
    
    saveSizePreference() {
      try {
        localStorage.setItem('actionReasons_modalSize', this.modalSize);
      } catch (error) {
        console.warn('Failed to save modal size preference:', error);
      }
    },
    
    setModalSize(size) {
      if (this.sizePresets[size]) {
        this.modalSize = size;
        this.saveSizePreference();
        
        // Reposition tooltip if it's currently shown to ensure it stays within viewport
        if (this.activeTooltip) {
          this.$nextTick(() => {
            const tooltipElement = document.querySelector('.tooltip');
            if (!tooltipElement) return;
            
            const tooltipRect = tooltipElement.getBoundingClientRect();
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;
            const padding = 10;
            
            // Get current position
            let currentLeft = tooltipRect.left;
            let currentTop = tooltipRect.top;
            
            // Constrain to viewport bounds
            const maxLeft = viewportWidth - tooltipRect.width - padding;
            const maxTop = viewportHeight - tooltipRect.height - padding;
            
            let newLeft = Math.max(padding, Math.min(currentLeft, maxLeft));
            let newTop = Math.max(padding, Math.min(currentTop, maxTop));
            
            // Update position if needed
            if (newLeft !== currentLeft || newTop !== currentTop) {
              this.tooltipStyle = {
                left: `${newLeft + tooltipRect.width / 2}px`,
                top: `${newTop}px`,
                transform: 'translateX(-50%) translateY(0%)'
              };
            }
            
            // Update initial position for drag
            this.initialTooltipPosition = {
              left: newLeft,
              top: newTop
            };
          });
        }
      }
    }
  }
};
</script>

<style scoped>
.action-reasons-container {
  background: var(--card-bg);
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 4px 6px var(--shadow-color);
  margin: 20px 0;
  width: 100%;
  max-width: 100%;
}

.action-reasons-header {
  margin-bottom: 20px;
  text-align: center;
}

.action-reasons-header h3 {
  margin: 0 0 8px 0;
  color: var(--text-primary);
  font-size: 1.5rem;
  font-weight: 600;
}

.action-reasons-header p {
  margin: 0;
  color: var(--text-tertiary);
  font-size: 0.9rem;
}

.timeline-container {
  position: relative;
  width: 100%;
  overflow-x: auto;
  overflow-y: visible;
  display: flex;
  justify-content: center;
  padding-bottom: 30px; /* Space for time labels */
}

/* Desktop: fit to screen width */
@media (min-width: 751px) {
  .timeline-container {
    overflow-x: hidden; /* No horizontal scroll on desktop */
    padding: 10px 20px 40px 20px; /* Add side padding for proper spacing */
    position: relative; /* Ensure proper stacking context */
  }
  
  .timeline-wrapper {
    width: 100%;
    max-width: 100%;
  }
  
  .timeline-ticks,
  .time-labels {
    width: 100%;
    max-width: 100%;
    justify-content: space-between;
  }
  
  /* Enhanced desktop tooltip positioning */
  .tooltip {
    animation: tooltipFadeIn 0.2s ease-out;
  }
}

.timeline-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: max-content;
  /* Ensure perfect alignment */
  position: relative;
}

/* Desktop responsive wrapper */
@media (min-width: 751px) {
  .timeline-wrapper {
    width: 100%;
    min-width: 100%;
  }
}

.timeline-axis {
  position: absolute;
  top: 40px;
  left: 0;
  right: 0;
  height: 2px;
  background: #ddd;
  z-index: 1;
}

.timeline-ticks {
  display: flex;
  gap: 0;
  padding: 20px 0 10px 0;
  min-width: max-content;
  width: max-content;
  flex-wrap: nowrap;
  justify-content: center;
  position: relative;
  z-index: 2;
}

/* Desktop: responsive ticks */
@media (min-width: 751px) {
  .timeline-axis {
    left: 0;
    right: 0;
  }
  
  .timeline-ticks {
    width: 100%;
    min-width: 100%;
    justify-content: space-between;
    padding: 20px 0 10px 0;
    gap: 0;
  }
}

.time-labels {
  display: flex;
  gap: 0;
  min-width: max-content;
  width: max-content;
  flex-wrap: nowrap;
  justify-content: center;
  margin-top: 5px;
  position: relative;
  z-index: 2;
}

/* Desktop: responsive time labels */
@media (min-width: 751px) {
  .time-labels {
    width: 100%;
    min-width: 100%;
    justify-content: space-between;
    padding: 0;
    gap: 0;
  }
}

.time-label {
  width: 20px; /* Match the total dot width including padding */
  height: 25px;
  font-size: 0.65rem;
  color: var(--text-tertiary);
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  transform: rotate(-45deg);
  transform-origin: center;
  flex-shrink: 0;
  box-sizing: border-box;
}

.time-label.show-label {
  color: var(--text-secondary);
  font-weight: 600;
  font-size: 0.7rem;
}

/* Desktop: flexible label sizing */
@media (min-width: 751px) {
  .time-label {
    flex: 1;
    width: auto;
    margin: 0;
    font-size: 0.65rem;
    transform: rotate(-30deg);
    text-align: center;
  }
  
  .time-label.show-label {
    font-size: 0.7rem;
    font-weight: 600;
    color: var(--text-secondary);
  }
}

.timeline-tick {
  position: relative;
  width: 1.5px;
  height: 12px;
  background: #bbb;
  cursor: pointer;
  transition: all 0.2s ease;
  flex: 0 0 3px;
  margin: 0;
  border-radius: 1px;
  box-sizing: border-box;
}

.timeline-tick.major-tick {
  height: 20px;
  width: 1.5px;
  background: #bbb;
  margin-top: -8px;
}

.timeline-tick.has-action {
  background: #ff6b6b;
  box-shadow: 0 0 4px rgba(255, 107, 107, 0.4);
}

.timeline-tick.has-action.major-tick {
  background: #ff5252;
  box-shadow: 0 0 6px rgba(255, 107, 107, 0.6);
}

/* Color based on is_updated value */
.timeline-tick.is-updated {
  background: #ff6b6b;
  box-shadow: 0 0 4px rgba(255, 107, 107, 0.4);
}

.timeline-tick.is-updated.major-tick {
  background: #ff5252;
  box-shadow: 0 0 6px rgba(255, 107, 107, 0.6);
}

.timeline-tick.not-updated {
  background: #000000;
  box-shadow: 0 0 2px rgba(0, 0, 0, 0.3);
}

.timeline-tick.not-updated.major-tick {
  background: #000000;
  box-shadow: 0 0 3px rgba(0, 0, 0, 0.4);
}

.timeline-tick:hover {
  transform: scaleY(1.2);
  background: #777;
}

.timeline-tick.has-action:hover {
  background: #ff4444;
}

.timeline-tick.is-updated:hover {
  background: #ff4444;
}

.timeline-tick.not-updated:hover {
  background: #333333;
}

/* Desktop: flexible tick sizing */
@media (min-width: 751px) {
  .timeline-tick {
    flex: 0 0 3px;
    width: 1.5px;
    margin: 0;
  }
  
  .timeline-tick.major-tick {
    flex: 0 0 3px;
    width: 1.5px;
    height: 24px;
    margin-top: -12px;
  }
}


.tooltip {
  position: fixed;
  z-index: 10000;
  pointer-events: auto; /* Allow interaction with tooltip */
  max-height: 600px; /* Tripled from original 200px */
  overflow: hidden; /* Container doesn't scroll, content does */
  /* Ensure tooltip appears above all timeline elements */
  box-shadow: 0 12px 36px rgba(0, 0, 0, 0.3);
  border-radius: 8px;
  border: 2px solid #555;
  backdrop-filter: blur(4px);
  transition: box-shadow 0.2s ease;
}

/* Size-specific styling */
.tooltip.size-small {
  max-width: 400px;
  max-height: 300px;
}

.tooltip.size-small .tooltip-content {
  max-width: 400px;
  max-height: 270px;
}

.tooltip.size-medium {
  max-width: 700px;
  max-height: 450px;
}

.tooltip.size-medium .tooltip-content {
  max-width: 700px;
  max-height: 420px;
}

.tooltip.size-large {
  max-width: 1000px;
  max-height: 600px;
}

.tooltip.size-large .tooltip-content {
  max-width: 1000px;
  max-height: 570px;
}

.tooltip.size-full {
  max-width: 95vw;
  max-height: 80vh;
}

.tooltip.size-full .tooltip-content {
  max-width: 95vw;
  max-height: calc(80vh - 30px);
}

.tooltip.dragging {
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.4);
  cursor: move;
}

.tooltip-content {
  background: #333;
  color: white;
  padding: 20px 24px 20px 24px; /* Consistent padding */
  border-radius: 6px;
  font-size: 0.8rem;
  max-width: 1000px; /* Increased width for JSON formatting */
  min-height: 120px; /* Increased minimum height */
  max-height: 570px; /* Tripled max height */
  overflow-y: auto; /* Content scrolls */
  overflow-x: auto; /* Handle wide content */
  white-space: pre-wrap; /* Preserve formatting for JSON */
  word-wrap: break-word;
  line-height: 1.4; /* Optimized for code/JSON readability */
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace; /* Monospace for JSON */
  /* Custom scrollbar styling */
  scrollbar-width: thin;
  scrollbar-color: #666 #333;
}

.tooltip-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #555;
  cursor: move;
  user-select: none;
  -webkit-user-select: none;
  gap: 12px;
}

.tooltip-header-left {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
}

.drag-handle {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  opacity: 0.7;
  transition: opacity 0.2s ease, color 0.2s ease;
  flex-shrink: 0;
  cursor: move;
}

.tooltip-header:hover .drag-handle {
  opacity: 1;
  color: #ccc;
}

.tooltip.dragging .drag-handle {
  opacity: 1;
  color: #ff6b6b;
}

.tooltip-header-center {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  text-align: center;
}

.drag-hint {
  font-size: 0.7rem;
  color: #aaa;
  font-weight: normal;
  font-style: italic;
  white-space: nowrap;
  transition: color 0.2s ease;
  display: inline-block;
  pointer-events: none;
  user-select: none;
  -webkit-user-select: none;
}

.tooltip-header:hover .drag-hint {
  color: #ccc;
}

.tooltip.dragging .drag-hint {
  color: #ff6b6b;
}

.tooltip-content h4 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #ff6b6b;
  white-space: nowrap;
  flex: 0 0 auto;
}

.close-button {
  background: none;
  border: none;
  color: #ccc;
  font-size: 1.5rem;
  font-weight: bold;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s ease;
  line-height: 1;
  flex-shrink: 0;
  z-index: 1;
  position: relative;
}

.close-button:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
}

.close-button:active {
  background: rgba(255, 255, 255, 0.2);
}

.tooltip-content p {
  margin: 0;
  line-height: 1.4;
}

.tooltip-content .no-action {
  color: #ccc;
  font-style: italic;
}

.tooltip-content .reason-text {
  margin: 8px 0 0 0;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.75rem;
  line-height: 1.4;
  color: #e8e8e8;
  background: rgba(0, 0, 0, 0.3);
  padding: 12px;
  border-radius: 4px;
  border-left: 3px solid #ff6b6b;
  overflow-x: auto;
  white-space: pre-wrap;
  word-wrap: break-word;
  max-height: 500px;
  overflow-y: auto;
}

/* Custom scrollbars for tooltip content */
.tooltip-content::-webkit-scrollbar,
.tooltip-content .reason-text::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.tooltip-content::-webkit-scrollbar-track,
.tooltip-content .reason-text::-webkit-scrollbar-track {
  background: #444;
  border-radius: 4px;
}

.tooltip-content::-webkit-scrollbar-thumb,
.tooltip-content .reason-text::-webkit-scrollbar-thumb {
  background: #666;
  border-radius: 4px;
}

.tooltip-content::-webkit-scrollbar-thumb:hover,
.tooltip-content .reason-text::-webkit-scrollbar-thumb:hover {
  background: #777;
}

.tooltip-content::-webkit-scrollbar-corner,
.tooltip-content .reason-text::-webkit-scrollbar-corner {
  background: #444;
}

/* Size Controls Styling */
.size-controls {
  display: flex;
  gap: 6px;
  margin-bottom: 12px;
  padding: 8px 0;
  border-bottom: 1px solid #555;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
}

.size-controls-label {
  color: #aaa;
  font-size: 0.7rem;
  font-weight: 500;
  margin-right: 4px;
  white-space: nowrap;
  user-select: none;
  -webkit-user-select: none;
}

.size-button {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid #666;
  color: #ccc;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.65rem;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 600;
  text-transform: uppercase;
  user-select: none;
  -webkit-user-select: none;
  min-width: 28px;
}

.size-button:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: #888;
  color: #fff;
}

.size-button.active {
  background: #ff6b6b;
  border-color: #ff6b6b;
  color: white;
  font-weight: 600;
}

.size-button:active {
  transform: scale(0.95);
}


/* Ensure timeline fits properly */
.timeline-container::-webkit-scrollbar {
  height: 8px;
}

.timeline-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.timeline-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.timeline-container::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Mobile scrollbar improvements */
@media (max-width: 750px) {
  .timeline-container::-webkit-scrollbar {
    height: 6px;
  }
  
  .timeline-container::-webkit-scrollbar-thumb {
    background: #999;
    border-radius: 3px;
  }
  
  .timeline-container::-webkit-scrollbar-track {
    background: #e5e5e5;
    border-radius: 3px;
  }
}
@media (max-width: 750px) {
  .action-reasons-container {
    width: 100%;
    padding: 16px;
    margin: 10px 0;
    position: relative; /* Changed from absolute */
  }

  .action-reasons-header {
    margin-bottom: 16px;
    text-align: center;
  }

  .action-reasons-header h3 {
    margin: 0 0 8px 0;
    color: #333;
    font-size: 1rem; /* Increased from 0.5rem */
    font-weight: 600;
  }

  .action-reasons-header p {
    margin: 0;
    color: #666;
    font-size: 0.75rem; /* Increased from 0.4rem */
    line-height: 1.3;
  }

  .timeline-container {
    padding: 10px 0 40px 0;
    touch-action: pan-x;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    /* Ensure scrolling works from the start */
    scroll-snap-type: x mandatory;
  }
  
  .timeline-wrapper {
    /* Calculate total width for all 288 intervals - ensure it's wider than viewport */
    width: calc(288 * 4px + 32px);
    min-width: calc(288 * 4px + 32px);
    display: flex;
    flex-direction: column;
  }

  .timeline-axis {
    top: 36px;
    left: 16px;
    right: 16px;
  }
  
  .timeline-ticks {
    padding: 16px 16px 8px 16px;
    width: 100%;
    justify-content: space-between;
  }
  
  .time-labels {
    margin-top: 8px;
    width: 100%;
    justify-content: space-between;
    padding: 0 16px;
  }
  
  .time-label {
    flex: 1;
    font-size: 0.6rem;
    height: 30px;
    max-width: 40px;
    min-width: 20px;
  }
  
  .time-label.show-label {
    font-size: 0.65rem;
    font-weight: 600;
  }

  .timeline-tick {
    width: 1.5px;
    height: 16px;
    margin: 0;
    flex: 0 0 3px;
    box-sizing: border-box;
  }
  
  .timeline-tick.major-tick {
    height: 24px;
    width: 1.5px;
    margin-top: -8px;
    flex: 0 0 3px;
  }

  .timeline-tick:hover,
  .timeline-tick:active {
    transform: scaleY(1.3) scaleX(1.5);
  }

  /* Tooltip adjustments for small screens */
  .tooltip {
    position: fixed;
    left: 50% !important;
    top: 10px !important; /* Always at top of screen to avoid overlap */
    transform: translateX(-50%) !important;
    max-width: 95vw !important;
    pointer-events: auto !important; /* Allow interaction on mobile */
    z-index: 10000;
    max-height: 80vh; /* Increased to 80vh for better JSON display */
    overflow: hidden; /* Container doesn't scroll */
    /* Enhanced mobile styling */
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
    border: 2px solid #777;
  }

  .tooltip-content {
    width: auto;
    min-width: 250px;
    max-width: 95vw;
    min-height: 150px; /* Increased minimum height for JSON */
    max-height: 75vh; /* Allow content to scroll */
    font-size: 0.75rem; /* Optimized for JSON readability */
    text-align: left; /* Better readability */
    word-wrap: break-word;
    white-space: pre-wrap; /* Preserve JSON formatting on mobile */
    padding: 16px 20px; /* Adjusted padding for mobile */
    line-height: 1.4; /* Optimized for code readability */
    overflow-y: auto; /* Content scrolls on mobile */
    overflow-x: auto; /* Handle wide content */
  }
  
  .tooltip-content .reason-text {
    font-size: 0.7rem; /* Smaller font for mobile JSON */
    padding: 8px;
    max-height: 50vh; /* Scrollable JSON area */
    overflow-y: auto;
    overflow-x: auto;
  }

  .tooltip-content h4 {
    font-size: 1rem; /* Increased font size */
    margin-bottom: 8px;
  }
  
  .drag-hint {
    font-size: 0.65rem;
  }
  
  .drag-handle {
    width: 10px;
    height: 10px;
  }
  
  .drag-handle svg {
    width: 10px;
    height: 10px;
  }
  
  .tooltip-header-left {
    gap: 6px;
  }

  .tooltip-content p {
    font-size: 0.85rem; /* Slightly larger text */
  }
  
  /* Size controls on mobile */
  .size-controls {
    gap: 4px;
    padding: 6px 0;
    margin-bottom: 10px;
  }
  
  .size-controls-label {
    font-size: 0.65rem;
    margin-right: 3px;
  }
  
  .size-button {
    padding: 3px 6px;
    font-size: 0.6rem;
    min-width: 26px;
  }
}

/* Additional mobile optimizations for very small screens */
@media (max-width: 480px) {
  .action-reasons-container {
    padding: 12px;
    margin: 8px 0;
  }

  .timeline-ticks {
    padding: 12px 8px 6px 8px;
    width: 100%;
    justify-content: space-between;
  }
  
  .time-labels {
    width: 100%;
    justify-content: space-between;
    padding: 0 8px;
  }
  
  .time-label {
    flex: 1;
    font-size: 0.55rem;
    height: 25px;
    max-width: 35px;
    min-width: 15px;
  }
  
  .time-label.show-label {
    font-size: 0.6rem;
    font-weight: 600;
  }

  .timeline-tick {
    width: 1.5px;
    height: 14px;
    flex: 0 0 3px;
    box-sizing: border-box;
  }
  
  .timeline-tick.major-tick {
    height: 20px;
    width: 1.5px;
    margin-top: -6px;
    flex: 0 0 3px;
  }

  .tooltip-content {
    min-width: 200px;
    max-width: 92vw;
    font-size: 0.75rem;
    padding: 12px 16px;
  }

  .tooltip-content h4 {
    font-size: 0.9rem;
  }

  .tooltip-content p {
    font-size: 0.8rem;
  }
}

/* Landscape mobile optimization */
@media (max-width: 750px) and (orientation: landscape) {
  .action-reasons-container {
    padding: 12px;
  }

  .timeline-dots {
    padding: 8px 0;
  }

  .tooltip {
    max-height: 80vh; /* Increased for landscape mode */
    overflow-y: auto;
  }

  .tooltip-content {
    max-height: 75vh; /* Increased for landscape mode */
    overflow-y: auto;
  }
}

/* Debug alignment - remove this in production if not needed */
.timeline-wrapper::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to right, transparent 19px, rgba(0,0,0,0.05) 19px, rgba(0,0,0,0.05) 20px, transparent 20px);
  background-size: 20px 100%;
  pointer-events: none;
  z-index: 1;
  opacity: 0; /* Hidden by default, can be enabled for debugging */
}

/* Show grid on hover for debugging (optional) */
.timeline-wrapper:hover::before {
  opacity: 0.3;
}

/* Tooltip animations */
@keyframes tooltipFadeIn {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(-100%) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(-100%) scale(1);
  }
}

/* Force scrollable area on mobile */
@media (max-width: 750px) {
  .timeline-container {
    scroll-behavior: smooth;
    -webkit-overflow-scrolling: touch;
    /* Force horizontal scroll */
    overflow-x: scroll;
  }
  
  /* Force scrollable content - much wider than viewport */
  .timeline-wrapper {
    min-width: 200vw !important;
    width: 200vw !important;
  }
}
</style>